import { ReactNode, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import {
	AssignStatus,
	InclusionFilterStatus,
	PriorityFilterStatus,
	SelectType,
	SignalFilterStatus,
} from "@/enum/list.enum";
import {
	BulkFindPersonRequest,
	BulkFindPersonResponse,
	BulkFindPersonSelection,
	useBulkFindPersonMutation,
} from "@/pages/Private/redux/list/list-find-person.api";
import { describeSaveFailure } from "@/utils/describeSaveFailure";

import { IFilterWithValues } from "../../FilterModal";
import { FindPeopleDialog } from "../../FindPeopleDialog/FindPeopleDialog";

interface UseBulkFindPersonParams {
	listId?: number;
	selectedItems: number[];
	selectType: SelectType;
	filter?: IFilterWithValues;
	inclusionStatus?: InclusionFilterStatus[];
	priorityFilterStatus?: PriorityFilterStatus[];
	assignStatus?: AssignStatus;
	/** The table's search: a select-all made while searching covers the matches only (PREC-4443). */
	searchValue?: string;
	/** Every status of the tab the table showed; the Assigned tab stands for several (PREC-4450). */
	assignStatuses?: AssignStatus[];
	/** The reason sub-filters the table showed, "STATUS:filterParam" (PREC-4450). */
	exclusionReasons?: string[];
	signalFilterStatus?: SignalFilterStatus[];
	/** True when the selection contains at least one placeholder ("SELECT"/"Person not found") row. */
	hasPlaceholderItems: boolean;
	openSnackbar: (text: string) => void;
	/**
	 * Selection state hooks for the post-queue cleanup. Clearing selectedItems alone is not enough:
	 * with select-all active the table's sync effect re-selects every row, so select-all must be
	 * toggled off and the scope reset first (same cleanup as the bulk deep-research success path).
	 */
	selection: {
		selectAll: boolean;
		toggleSelectAll: () => void;
		setSelectType: (selectType: SelectType) => void;
		setSelectedItems: (items: number[]) => void;
	};
}

interface BulkFindPerson {
	/** The bulk-action-footer prop, or undefined when the action should be hidden. */
	bulkFindPerson?: { bulkFindPerson: () => void; bulkFindPersonIsLoading: boolean };
	/** The find people dialog to render once in the table, while it is open. */
	modal: ReactNode;
}

type BulkFindPersonTableSelection = Pick<
	UseBulkFindPersonParams,
	| "selectedItems"
	| "selectType"
	| "filter"
	| "inclusionStatus"
	| "priorityFilterStatus"
	| "assignStatus"
	| "searchValue"
	| "assignStatuses"
	| "exclusionReasons"
	| "signalFilterStatus"
>;

/** One opening of the dialog, with the selection it was opened for. */
interface DialogSession {
	id: number;
	selection: BulkFindPersonSelection;
}

/**
 * The rows a bulk find person is about: the picked ids, plus for a select-all the table's filters,
 * tab and search, from which the server re-derives the set (PREC-4443). The plan and the search
 * both take it.
 */
export const buildBulkFindPersonSelection = (
	listId: number,
	selection: BulkFindPersonTableSelection
): BulkFindPersonSelection => ({
	listId,
	ids: selection.selectedItems,
	selectType: selection.selectType,
	...(selection.selectType === SelectType.ALL && {
		filter: selection.filter,
		inclusionFilterStatus: selection.inclusionStatus ?? undefined,
		priorityFilterStatus: selection.priorityFilterStatus ?? undefined,
		assignStatus: selection.assignStatus ?? undefined,
		searchValue: selection.searchValue,
		assignStatuses: selection.assignStatuses,
		exclusionReasons: selection.exclusionReasons,
		signalFilterStatus: selection.signalFilterStatus,
	}),
});

/** Whether two selections name the same rows of the same view (for clearing it after a search). */
const sameSelection = (a: BulkFindPersonSelection, b: BulkFindPersonSelection): boolean =>
	JSON.stringify(a) === JSON.stringify(b);

/**
 * Owns the lead-list bulk "Find person" wiring (PREC-4398): the dialog's open state, the search
 * mutation and the selection it is for. The selection, and with it the list, is taken when the
 * dialog opens and kept for that opening, so the plan and the search are about the same rows of
 * the same list; a table that moves to another list closes the dialog. Each opening mounts a new
 * dialog (it seeds from the list, PREC-4432). The answer of a search belongs to the opening that
 * started it: when the dialog was closed (or opened again) meanwhile, the run still started, so it
 * is reported in the snackbar instead of in a dialog that is gone (apps/web CLAUDE.md §23), and
 * the table's selection is cleared only while it is still the one that was searched. Rows update
 * over the websocket; the table refetches on the bulk-find-person-updated events.
 */
export const useBulkFindPerson = ({
	listId,
	hasPlaceholderItems,
	openSnackbar,
	selection,
	...table
}: UseBulkFindPersonParams): BulkFindPerson => {
	const { t } = useTranslation();
	const ts = (key: string, options?: Record<string, unknown>) =>
		t(`defineIcps.bulkFindPeople.${key}`, options ?? {});
	const [session, setSession] = useState<DialogSession | null>(null);
	const sessionRef = useRef<DialogSession | null>(null);
	const openingsRef = useRef(0);
	const currentSelectionRef = useRef<BulkFindPersonSelection | null>(null);
	const [bulkFindPerson, { isLoading }] = useBulkFindPersonMutation();

	currentSelectionRef.current = listId ? buildBulkFindPersonSelection(listId, table) : null;

	const setOpenSession = (next: DialogSession | null) => {
		sessionRef.current = next;
		setSession(next);
	};

	// A dialog opened on one list is not carried to another: the table moved, so it closes.
	if (session && session.selection.listId !== listId) {
		setOpenSession(null);
	}

	const open = () => {
		if (!listId) {
			return;
		}

		openingsRef.current += 1;
		setOpenSession({
			id: openingsRef.current,
			selection: buildBulkFindPersonSelection(listId, table),
		});
	};

	/** Clears the table's selection, unless the user selected other rows since the search began. */
	const clearSelection = (searched: BulkFindPersonSelection) => {
		const current = currentSelectionRef.current;

		if (!current || !sameSelection(current, searched)) {
			return;
		}

		if (selection.selectAll) {
			selection.toggleSelectAll();
		}

		selection.setSelectType(SelectType.CURRENT_PAGE);
		selection.setSelectedItems([]);
	};

	const submit = async (
		opening: DialogSession,
		request: BulkFindPersonRequest
	): Promise<BulkFindPersonResponse> => {
		const stillOpen = () => sessionRef.current?.id === opening.id;

		try {
			const response = await bulkFindPerson(request).unwrap();

			clearSelection(opening.selection);

			if (!stillOpen()) {
				openSnackbar(
					ts("startedInBackground", { count: response.queued, skipped: response.skipped })
				);
			}

			return response;
		} catch (error) {
			if (!stillOpen()) {
				openSnackbar(describeSaveFailure(error, ts("startFailed")));
			}

			throw error;
		}
	};

	const shown = session && session.selection.listId === listId ? session : null;

	return {
		bulkFindPerson:
			hasPlaceholderItems && listId
				? { bulkFindPerson: open, bulkFindPersonIsLoading: isLoading }
				: undefined,
		modal: shown ? (
			<FindPeopleDialog
				key={shown.id}
				listId={shown.selection.listId}
				selection={shown.selection}
				sessionKey={`${shown.selection.listId}-${shown.id}`}
				onClose={() => setOpenSession(null)}
				onSubmit={(request) => submit(shown, request)}
			/>
		) : null,
	};
};
